import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { albumData } from '@/mocks/album';
import { useLanguage } from '@/i18n/useLanguage';

export default function AlbumOverview() {
  const { t } = useTranslation();
  const lang = useLanguage();

  return (
    <section className="py-20 md:py-28 bg-background-50">
      <div className="max-w-4xl mx-auto px-4 md:px-6">
        <div className="text-center mb-14">
          <p className="text-xs tracking-[0.3em] text-secondary-500 mb-3">OVERVIEW</p>
          <h2 className="text-2xl md:text-3xl font-heading text-foreground-950">{t('overview.title')}</h2>
        </div>

        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
          <div className="flex-1">
            <p className="text-foreground-700 leading-loose text-sm md:text-base whitespace-pre-line">
              {albumData.description[lang]}
            </p>

            <div className="mt-10 grid grid-cols-2 gap-y-4 gap-x-8">
              <div>
                <p className="text-xs text-secondary-500 tracking-wider mb-1">{t('overview.artist')}</p>
                <p className="text-sm text-foreground-900">{albumData.artist}</p>
              </div>
              <div>
                <p className="text-xs text-secondary-500 tracking-wider mb-1">{t('overview.releaseDate')}</p>
                <p className="text-sm text-foreground-900">{albumData.releaseDate}</p>
              </div>
              <div>
                <p className="text-xs text-secondary-500 tracking-wider mb-1">{t('overview.label')}</p>
                <p className="text-sm text-foreground-900">{albumData.label}</p>
              </div>
              <div>
                <p className="text-xs text-secondary-500 tracking-wider mb-1">{t('overview.genre')}</p>
                <p className="text-sm text-foreground-900">{albumData.genre}</p>
              </div>
            </div>

            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link
                to="/lyrics"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-background-200/60 text-foreground-900 hover:bg-background-300/80 border border-background-300/40 transition-all duration-300 text-sm font-medium whitespace-nowrap cursor-pointer"
              >
                <i className="ri-file-music-line"></i>
                {t('overview.viewLyrics')}
              </Link>
              <Link
                to="/liner-notes"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-background-200/60 text-foreground-900 hover:bg-background-300/80 border border-background-300/40 transition-all duration-300 text-sm font-medium whitespace-nowrap cursor-pointer"
              >
                <i className="ri-book-open-line"></i>
                {t('overview.readLinerNotes')}
              </Link>
            </div>
          </div>

          <div className="lg:w-72 flex-shrink-0">
            <div className="bg-background-100 rounded-lg p-6 border border-background-300/30">
              <h3 className="text-sm font-medium text-foreground-900 mb-5 flex items-center gap-2">
                <i className="ri-group-line text-secondary-500"></i>
                {t('overview.credits')}
              </h3>
              <dl className="space-y-3">
                {Object.entries(albumData.credit).map(([role, name]) => {
                  const labelMap: Record<string, string> = {
                    lyricsConcept: 'Lyrics / Concept',
                    music: 'Music',
                    vocal: 'Vocal',
                    artDirection: 'Art Direction',
                    photography: 'Photography',
                  };
                  return (
                    <div key={role} className="flex justify-between items-baseline gap-4">
                      <dt className="text-xs text-secondary-500 whitespace-nowrap">{labelMap[role] || role}</dt>
                      <dd className="text-xs text-foreground-700 text-right">{name}</dd>
                    </div>
                  );
                })}
              </dl>

              {/* AI の関与の開示。文言の正本は music-box の ARTIST.md（アーティスト単位）で、
                  album.generated.ts へ生成されて届く。ここに直書きしないこと。
                  **両言語とも常に出す**（開示なので片方だけ見える状態を作らない）。
                  表示中の言語を主、もう一方を従に置く。 */}
              <div className="mt-5 pt-5 border-t border-background-300/30">
                <p className="text-xs text-foreground-700 leading-relaxed">
                  {albumData.disclosure[lang]}
                </p>
                <p className="mt-1.5 text-[11px] text-secondary-500 leading-relaxed">
                  {albumData.disclosure[lang === 'ja' ? 'en' : 'ja']}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}